import {
  Chip,
  Avatar,
  Button,
  Box,
  Typography,
  useTheme,
  Menu,
  MenuItem,
  Popover,
  Divider,
} from "@mui/material";
import { getBackgroundColor } from "@/pages/Board/utils";
import { getInitials } from "@/pages/Board/components/TaskLayout";
import {
  Cancel as CancelIcon,
  FilterAltOff as FilterAltOffIcon,
  Search as SearchIcon,
  LocalOffer as TagIcon,
  CheckCircle as StatusIcon,
  LowPriority as PriorityIcon,
  DateRange as DateRangeIcon,
  PlayArrow as StartDateIcon,
  Update as UpdateIcon,
  FolderOpen as ProjectIcon,
  MoreHoriz as MoreIcon,
} from "@mui/icons-material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LoopOutlinedIcon from "@mui/icons-material/LoopOutlined";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "@/app/hooks";
import {
  selectSearchQuery,
  selectFilters,
  selectApiData,
} from "@/features/bughurd/boardSelector";
import {
  setSearchQuery,
  toggleAvatar,
  toggleTag,
  setSelectedStatuses,
  setSelectedPriorities,
  setSelectedProjects,
  setDueDateFilter,
  setStartDateFilter,
  setUpdatedDateFilter,
  clearAllFilters,
  setIsRecurring,
  setSelectedCreatedBy,
} from "@/features/bughurd/slice";
import { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";

export const FilterChips = () => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { id: projectId } = useParams();

  // State for managing "More" popup
  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);
  const [visibleChips, setVisibleChips] = useState<number>(4);
  const containerRef = useRef<HTMLDivElement>(null);

  // Redux selectors
  const searchQuery = useAppSelector(selectSearchQuery);
  const filters = useAppSelector(selectFilters);
  const { statusList, companyMemberData, projects } =
    useAppSelector(selectApiData);

  // Destructure filters from Redux
  const {
    selectedAvatars,
    selectedTags,
    selectedStatuses,
    selectedPriorities,
    selectedProjects,
    selectedDueDate,
    selectedStartDate,
    selectedUpdatedDate,
    selectedCreatedBy,
    dueDateStart,
    dueDateEnd,
    startDateStart,
    startDateEnd,
    updatedAtStart,
    updatedAtEnd,
    isRecurring,
  } = filters;

  // Create options from Redux data
  const members = companyMemberData || [];
  const statusOptions = (statusList || []).map((status: any) => ({
    _id: status._id,
    value: status._id,
    label: status.status,
    status: status.status,
  }));
  const priorityOptions = [
    { value: "Low", label: t("Low") },
    { value: "Medium", label: t("Medium") },
    { value: "High", label: t("High") },
    { value: "Highest", label: t("Highest") },
  ];

  // Action handlers
  const handleClearSearch = () => {
    dispatch(setSearchQuery(""));
  };
  const handleClearRecurring = () => {
    dispatch(setIsRecurring(false));
  };
  const handleClearAvatar = (userId: string) => {
    dispatch(toggleAvatar(userId));
  };

  const handleClearTag = (tag: string) => {
    dispatch(toggleTag(tag));
  };

  const handleClearStatus = (status: string) => {
    const newStatuses = selectedStatuses.filter((s) => s !== status);
    dispatch(setSelectedStatuses(newStatuses));
  };

  const handleClearPriority = (priority: string) => {
    const newPriorities = selectedPriorities.filter((p) => p !== priority);
    dispatch(setSelectedPriorities(newPriorities));
  };

  const handleClearProject = (projectId: string) => {
    const newProjects = selectedProjects.filter((p) => p !== projectId);
    dispatch(setSelectedProjects(newProjects));
  };

  const handleClearCreatedBy = (userId: string) => {
    const newCreatedBy = selectedCreatedBy.filter((id) => id !== userId);
    dispatch(setSelectedCreatedBy(newCreatedBy));
  };

  const handleClearDueDate = () => {
    dispatch(
      setDueDateFilter({
        selectedDueDate: "",
        dueDateStart: "",
        dueDateEnd: "",
      })
    );
  };

  const handleClearStartDate = () => {
    dispatch(
      setStartDateFilter({
        selectedStartDate: "",
        startDateStart: "",
        startDateEnd: "",
      })
    );
  };

  const handleClearUpdatedDate = () => {
    dispatch(
      setUpdatedDateFilter({
        selectedUpdatedDate: "",
        updatedAtStart: "",
        updatedAtEnd: "",
      })
    );
  };

  const handleClearAll = () => {
    dispatch(clearAllFilters());
    setMoreAnchorEl(null);
  };

  // Helper function to format date range display
  const formatDateRange = (startDate?: string, endDate?: string) => {
    if (!startDate && !endDate) return "";
    if (startDate === endDate) return startDate;
    return `${startDate || ""} - ${endDate || ""}`;
  };

  // Helper function to get date filter label
  const getDateFilterLabel = (
    filterType: string,
    option: string,
    startDate?: string,
    endDate?: string
  ) => {
    const dateLabels: Record<string, string> = {
      today: t("Today"),
      yesterday: t("Yesterday"),
      tomorrow: t("Tomorrow"),
      this_week: t("This week"),
      last_7_days: t("Last 7 days"),
      next_7_days: t("Next 7 days"),
      next_week: t("Next Week"),
      date_range: formatDateRange(startDate, endDate),
      no_date: t("No date"),
    };

    const label = dateLabels[option] || option;
    return `${filterType}: ${label}`;
  };

  // Handle More button click
  const handleMoreClick = (event: React.MouseEvent<HTMLElement>) => {
    setMoreAnchorEl(event.currentTarget);
  };

  const handleMoreClose = () => {
    setMoreAnchorEl(null);
  };

  // Create all filter chips data organized by type
  const createAllChipsData = () => {
    const allChips: Array<{
      id: string;
      type: string;
      element: JSX.Element;
      onDelete: () => void;
      label: string;
    }> = [];

    // Search chip
    if (searchQuery) {
      allChips.push({
        id: `search-${searchQuery}`,
        type: "search",
        label: `"${searchQuery}"`,
        onDelete: handleClearSearch,
        element: (
          <Chip
            key="search"
            label={`${t("Search:")} "${searchQuery}"`}
            onDelete={handleClearSearch}
            deleteIcon={<CancelIcon fontSize="small" />}
            variant="outlined"
            size="small"
            icon={<SearchIcon fontSize="small" />}
          />
        ),
      });
    }

    // Avatar chips (Assignee)
    selectedAvatars.forEach((userId) => {
      if (userId === "unassigned") {
        allChips.push({
          id: `avatar-unassigned`,
          type: "assignee",
          label: t("Unassigned"),
          onDelete: () => handleClearAvatar("unassigned"),
          element: (
            <Chip
              key="unassigned"
              avatar={
                <Avatar
                  sx={{
                    width: 20,
                    height: 20,
                    fontSize: "10px",
                    display: "flex",
                    backgroundColor: "#9e9e9e",
                  }}
                >
                  <PersonOutlineIcon style={{ fontSize: "14px" }} />
                </Avatar>
              }
              label={t("Unassigned")}
              onDelete={() => handleClearAvatar("unassigned")}
              deleteIcon={<CancelIcon />}
              variant="outlined"
              size="small"
            />
          ),
        });
      } else {
        const user = members.find((u: any) => u._id === userId);
        allChips.push({
          id: `avatar-${userId}`,
          type: "assignee",
          label: user?.fullName || userId,
          onDelete: () => handleClearAvatar(userId),
          element: (
            <Chip
              key={userId}
              avatar={
                <Avatar
                  sx={{
                    width: 20,
                    height: 20,
                    fontSize: "10px",
                    display: "flex",
                    backgroundColor: getBackgroundColor(userId, projectId),
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 500,
                      color: theme.palette.background.default,
                      fontSize: "8px",
                    }}
                  >
                    {getInitials(user?.fullName || "")}
                  </Typography>
                </Avatar>
              }
              label={user?.fullName || userId}
              onDelete={() => handleClearAvatar(userId)}
              deleteIcon={<CancelIcon />}
              variant="outlined"
              size="small"
            />
          ),
        });
      }
    });

    selectedCreatedBy.forEach((userId) => {
      const user = members.find((u: any) => u._id === userId);
      allChips.push({
        id: `created-by-${userId}`,
        type: "created-by",
        label: user?.fullName || userId,
        onDelete: () => handleClearCreatedBy(userId),
        element: (
          <Chip
            key={`created-by-${userId}`}
            avatar={
              <Avatar
                sx={{
                  width: 20,
                  height: 20,
                  fontSize: "10px",
                  display: "flex",
                  backgroundColor: getBackgroundColor(userId, projectId),
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 500,
                    color: theme.palette.background.default,
                    fontSize: "8px",
                  }}
                >
                  {getInitials(user?.fullName || "")}
                </Typography>
              </Avatar>
            }
            label={`${t("Created by:")} ${user?.fullName || userId}`}
            onDelete={() => handleClearCreatedBy(userId)}
            deleteIcon={<CancelIcon />}
            variant="outlined"
            size="small"
            icon={<PersonAddIcon fontSize="small" />}
          />
        ),
      });
    });

    // Tag chips
    selectedTags.forEach((tag) => {
      allChips.push({
        id: `tag-${tag}`,
        type: "tag",
        label: tag,
        onDelete: () => handleClearTag(tag),
        element: (
          <Chip
            key={tag}
            label={tag}
            onDelete={() => handleClearTag(tag)}
            deleteIcon={<CancelIcon fontSize="small" />}
            variant="outlined"
            size="small"
            icon={<TagIcon fontSize="small" />}
          />
        ),
      });
    });

    // Status chips
    selectedStatuses.forEach((status) => {
      const statusLabel =
        statusOptions.find((s) => s._id === status)?.status || status;
      allChips.push({
        id: `status-${status}`,
        type: "status",
        label: statusLabel,
        onDelete: () => handleClearStatus(status),
        element: (
          <Chip
            key={status}
            label={`${t("Status:")} ${statusLabel}`}
            onDelete={() => handleClearStatus(status)}
            deleteIcon={<CancelIcon fontSize="small" />}
            variant="outlined"
            size="small"
            icon={<StatusIcon fontSize="small" />}
          />
        ),
      });
    });

    // Priority chips
    selectedPriorities.forEach((priority) => {
      const priorityLabel =
        priorityOptions.find((p) => p.value === priority)?.label || priority;
      allChips.push({
        id: `priority-${priority}`,
        type: "priority",
        label: priorityLabel,
        onDelete: () => handleClearPriority(priority),
        element: (
          <Chip
            key={priority}
            label={`${t("Priority:")} ${priorityLabel}`}
            onDelete={() => handleClearPriority(priority)}
            deleteIcon={<CancelIcon fontSize="small" />}
            variant="outlined"
            size="small"
            icon={<PriorityIcon fontSize="small" />}
          />
        ),
      });
    });

    // Project chips
    selectedProjects.forEach((projectId) => {
      const project = projects?.find((p: any) => p._id === projectId);
      allChips.push({
        id: `project-${projectId}`,
        type: "project",
        label: project?.name || projectId,
        onDelete: () => handleClearProject(projectId),
        element: (
          <Chip
            key={projectId}
            label={`${t("Project:")} ${project?.name || projectId}`}
            onDelete={() => handleClearProject(projectId)}
            deleteIcon={<CancelIcon fontSize="small" />}
            variant="outlined"
            size="small"
            icon={<ProjectIcon fontSize="small" />}
            sx={{
              "& .MuiChip-label": {
                maxWidth: "200px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            }}
          />
        ),
      });
    });

    // Date chips
    if (selectedDueDate) {
      allChips.push({
        id: `due-date-${selectedDueDate}`,
        type: "due-date",
        label: formatDateRange(dueDateStart, dueDateEnd) || selectedDueDate,
        onDelete: handleClearDueDate,
        element: (
          <Chip
            key="due-date"
            label={getDateFilterLabel(
              t("Due date"),
              selectedDueDate,
              dueDateStart,
              dueDateEnd
            )}
            onDelete={handleClearDueDate}
            deleteIcon={<CancelIcon fontSize="small" />}
            variant="outlined"
            size="small"
            icon={<DateRangeIcon fontSize="small" />}
            sx={{
              "& .MuiChip-label": {
                maxWidth: "200px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            }}
          />
        ),
      });
    }

    if (selectedStartDate) {
      allChips.push({
        id: `start-date-${selectedStartDate}`,
        type: "start-date",
        label:
          formatDateRange(startDateStart, startDateEnd) || selectedStartDate,
        onDelete: handleClearStartDate,
        element: (
          <Chip
            key="start-date"
            label={getDateFilterLabel(
              t("Start date"),
              selectedStartDate,
              startDateStart,
              startDateEnd
            )}
            onDelete={handleClearStartDate}
            deleteIcon={<CancelIcon fontSize="small" />}
            variant="outlined"
            size="small"
            icon={<StartDateIcon fontSize="small" />}
            sx={{
              "& .MuiChip-label": {
                maxWidth: "200px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            }}
          />
        ),
      });
    }

    if (selectedUpdatedDate) {
      allChips.push({
        id: `updated-date-${selectedUpdatedDate}`,
        type: "updated-date",
        label:
          formatDateRange(updatedAtStart, updatedAtEnd) || selectedUpdatedDate,
        onDelete: handleClearUpdatedDate,
        element: (
          <Chip
            key="updated-date"
            label={getDateFilterLabel(
              t("Updated date"),
              selectedUpdatedDate,
              updatedAtStart,
              updatedAtEnd
            )}
            onDelete={handleClearUpdatedDate}
            deleteIcon={<CancelIcon fontSize="small" />}
            variant="outlined"
            size="small"
            icon={<UpdateIcon fontSize="small" />}
            sx={{
              "& .MuiChip-label": {
                maxWidth: "200px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            }}
          />
        ),
      });
    }
    // Recurring chip
    if (isRecurring) {
      allChips.push({
        id: `recurring`,
        type: "recurring",
        label: t(`Recurring`),
        onDelete: handleClearRecurring,
        element: (
          <Chip
            key="recurring"
            label={t("Recurring")}
            onDelete={handleClearRecurring}
            deleteIcon={<CancelIcon fontSize="small" />}
            variant="outlined"
            size="small"
            icon={<LoopOutlinedIcon fontSize="small" />}
          />
        ),
      });
    }

    return allChips;
  };

  // Group chips by type for organized display in popover
  const groupChipsByType = (chips: ReturnType<typeof createAllChipsData>) => {
    const grouped: Record<string, typeof chips> = {};
    chips.forEach((chip) => {
      if (!grouped[chip.type]) {
        grouped[chip.type] = [];
      }
      grouped[chip.type].push(chip);
    });
    return grouped;
  };

  // Get filter type display names and icons
  const getFilterTypeInfo = (type: string) => {
    const typeInfo: Record<string, { label: string; icon: JSX.Element }> = {
      search: { label: t("Search"), icon: <SearchIcon fontSize="small" /> },
      assignee: {
        label: t("Assignee"),
        icon: <PersonOutlineIcon fontSize="small" />,
      },
      "created-by": {
        label: t("Created By"),
        icon: <PersonAddIcon fontSize="small" />,
      },
      tag: { label: t("Tags"), icon: <TagIcon fontSize="small" /> },
      status: { label: t("Status"), icon: <StatusIcon fontSize="small" /> },
      priority: {
        label: t("Priority"),
        icon: <PriorityIcon fontSize="small" />,
      },
      project: { label: t("Project"), icon: <ProjectIcon fontSize="small" /> },
      "due-date": {
        label: t("Due Date"),
        icon: <DateRangeIcon fontSize="small" />,
      },
      "start-date": {
        label: t("Start Date"),
        icon: <StartDateIcon fontSize="small" />,
      },
      "updated-date": {
        label: t("Updated Date"),
        icon: <UpdateIcon fontSize="small" />,
      },
      recurring: {
        label: t("Recurring"),
        icon: <LoopOutlinedIcon fontSize="small" />,
      },
    };
    return typeInfo[type] || { label: type, icon: <></> };
  };

  const allChips = createAllChipsData();
  const hasFilters = allChips.length > 0;
  const hasHiddenChips = allChips.length > visibleChips;
  const visibleChipsData = allChips.slice(0, visibleChips);
  const hiddenChipsData = allChips.slice(visibleChips);
  const groupedHiddenChips = groupChipsByType(hiddenChipsData);

  // Adjust visible chips based on container width
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const estimatedChipsPerRow = Math.floor((containerWidth - 200) / 120);
        setVisibleChips(Math.max(3, estimatedChipsPerRow));
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!hasFilters) return null;

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap",
        mt: 1.5
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 500, color: "text.secondary" }}
      >
        {t("Applied Filters:")}
      </Typography>

      {/* Clear all button */}
      <Button
        size="small"
        onClick={handleClearAll}
        startIcon={<FilterAltOffIcon fontSize="small" />}
        sx={{
          textTransform: "none",
          ml: 1,
          fontSize: "12px",
        }}
        variant="outlined"
      >
        {t("Clear all")}
      </Button>

      {/* Visible chips */}
      {visibleChipsData.map((chip) => chip.element)}

      {/* More button */}
      {hasHiddenChips && (
        <Chip
          label={`+${hiddenChipsData.length} ${t("more")}`}
          onClick={handleMoreClick}
          variant="outlined"
          size="small"
          icon={<MoreIcon fontSize="small" />}
          sx={{
            cursor: "pointer",
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        />
      )}

      {/* More filters popover */}
      <Popover
        open={Boolean(moreAnchorEl)}
        anchorEl={moreAnchorEl}
        onClose={handleMoreClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            maxWidth: 400,
            maxHeight: 400,
            overflow: "auto",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            {t("Additional Filters")}
          </Typography>

          {Object.entries(groupedHiddenChips).map(
            ([filterType, chips], index) => {
              const typeInfo = getFilterTypeInfo(filterType);
              return (
                <Box key={filterType}>
                  {/* Filter type header */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                      mt: index > 0 ? 2 : 0,
                    }}
                  >
                    {typeInfo.icon}
                    <Typography
                      variant="caption"
                      sx={{
                        ml: 1,
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {typeInfo.label}
                    </Typography>
                  </Box>

                  {/* Filter items */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      mb: 1,
                    }}
                  >
                    {chips.map((chip) => (
                      <Box
                        key={chip.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          p: 1,
                          borderRadius: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: "0.875rem",
                            color: "text.secondary",
                          }}
                        >
                          {chip.label}
                        </Typography>
                        <Button
                          size="small"
                          onClick={() => {
                            chip.onDelete();
                            // Close popover if no more hidden chips
                            if (hiddenChipsData.length === 1) {
                              handleMoreClose();
                            }
                          }}
                          sx={{
                            minWidth: "auto",
                            p: 0.5,
                            color: theme.palette.text.secondary,
                            "&:hover": {
                              backgroundColor: "transparent",
                            },
                          }}
                        >
                          <CancelIcon fontSize="small" />
                        </Button>
                      </Box>
                    ))}
                  </Box>

                  {/* Divider between different filter types */}
                  {index < Object.entries(groupedHiddenChips).length - 1 && (
                    <Divider sx={{ my: 1 }} />
                  )}
                </Box>
              );
            }
          )}
        </Box>
      </Popover>
    </Box>
  );
};
